import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { shallow } from "zustand/shallow";

import withWidgetChildrenLoader from "@/hocs/WidgetContainer/withWidgetChildrenLoader";
import useMetadataStore from "@/state/metadata";
import TooltipButton from "../layouts/TooltipButton";

import "@inovua/reactdatagrid-community/index.css";
import "./WidgetKeyIndicator.css";

import { pull } from "../../utils";

const Widget1 = ({ setLoading }) => {
  const { t, i18n } = useTranslation();

  const { orgUnits, orgUnitGroups } = useMetadataStore(
    (state) => ({
      orgUnits: state.oupOrgUnits,
      orgUnitGroups: state.oupOrgUnitGroups,
    }),
    shallow
  );

  const [dataNutri, setDataNutri] = useState();

  const getStyleByRangeOfValue = ({ red, yellow, green }) => {
    if (red) {
      return "#FF5757";
    }
    if (yellow) {
      return "#FFDE59";
    }
    if (green) {
      return "#99E573";
    }
    return "#C0C0C0";
  };

  const listLegend = [
    // sfjQ42ahvLG
    {
      red: { min: 0, max: 55, background: "#FF5757" },
      yellow: { min: 56, max: 72, background: "#FFDE59" },
      green: { min: 73, max: Infinity, background: "#99E573" },
    },
    // tvtnT71idQ4
    {
      red: { min: 19.6, max: Infinity, background: "#FF5757" },
      yellow: { min: 16.5, max: 19.5, background: "#FFDE59" },
      green: { min: 0, max: 16.4, background: "#99E573" },
    },
    // vTHzAXlQiF6 InstitutionalWasting
    {
      red: { min: 10, max: Infinity, background: "#FF5757" },
      yellow: { min: 5, max: 10, background: "#FFDE59" },
      green: { min: 0, max: 4.9, background: "#99E573" },
    },
    // uEYUCTNMfdk ANC coverage
    {
      red: { min: 0, max: 48, background: "#FF5757" },
      yellow: { min: 48.1, max: 60, background: "#FFDE59" },
      green: { min: 60.1, max: Infinity, background: "#99E573" },
    },
    // QOYBJGW55uA Pregnant women who received IFA tablets during ANC
    {
      red: { min: 0, max: 59, background: "#FF5757" },
      yellow: { min: 60, max: 68.9, background: "#FFDE59" },
      green: { min: 69, max: Infinity, background: "#99E573" },
    },
    // ivYamARkldu Pregnant women identified as having anaemia
    {
      red: { min: 22, max: Infinity, background: "#FF5757" },
      yellow: { min: 20, max: 21, background: "#FFDE59" },
      green: { min: 0, max: 19.9, background: "#99E573" },
    },
    // pS4nwJt7Qyv Low birth weight
    {
      red: { min: 11, max: Infinity, background: "#FF5757" },
      yellow: { min: 5, max: 10, background: "#FFDE59" },
      green: { min: 0, max: 4, background: "#99E573" },
    },
    // Early initiation of breastfeeding
    {
      red: { min: 0, max: 53.1, background: "#FF5757" },
      yellow: { min: 53.2, max: 68.4, background: "#FFDE59" },
      green: { min: 68.5, max: Infinity, background: "#99E573" },
    },
    // Exclusive breastfeeding until discharge
    {
      red: { min: 0, max: 53.1, background: "#FF5757" },
      yellow: { min: 53.2, max: 90, background: "#FFDE59" },
      green: { min: 91, max: Infinity, background: "#99E573" },
    },
    // Vitamin-A
    {
      red: { min: 0, max: 52, background: "#FF5757" },
      yellow: { min: 52, max: 63, background: "#FFDE59" },
      green: { min: 63, max: Infinity, background: "#99E573" },
    },
    // Deworming
    {
      red: { min: 0, max: Infinity, background: "#FF5757" },
      yellow: { min: 30.8, max: 36.4, background: "#FFDE59" },
      green: { min: 0, max: 30.7, background: "#99E573" },
    },
    // Children with SAM identified
    {
      red: { min: 1.9, max: Infinity, background: "#FF5757" },
      yellow: { min: 1.3, max: 1.8, background: "#FFDE59" },
      green: { min: 0, max: 1.2, background: "#99E573" },
    },
  ];

  const valueInRange = (value, range) => {
    return value >= range.min && value <= range.max;
  };

  const listData = useMemo(
    () => [
      {
        value: "1",
        getValue: (ouId) => {
          const value = dataNutri[ouId]?.find((rows) => {
            return rows[0] === "sfjQ42ahvLG";
          })?.[2];

          return value
            ? {
                de: "sfjQ42ahvLG",
                value: value,
                style: getStyleByRangeOfValue({
                  red: valueInRange(value, listLegend[0].red),
                  yellow: valueInRange(value, listLegend[0].yellow),
                  green: valueInRange(value, listLegend[0].green),
                }),
              }
            : null;
        },
      },
      {
        value: "2",
        getValue: (ouId) => {
          const value = dataNutri[ouId]?.find((rows) => {
            return rows[0] === "tvtnT71idQ4";
          })?.[2];

          return value
            ? {
                de: "tvtnT71idQ4",
                value: value,
                style: getStyleByRangeOfValue({
                  red: valueInRange(value, listLegend[1].red),
                  yellow: valueInRange(value, listLegend[1].yellow),
                  green: valueInRange(value, listLegend[1].green),
                }),
              }
            : null;
        },
      },
      {
        value: "3",
        getValue: (ouId) => {
          const value = dataNutri[ouId]?.find((rows) => {
            return rows[0] === "vTHzAXlQiF6";
          })?.[2];

          return value
            ? {
                de: "vTHzAXlQiF6",
                value: value,
                style: getStyleByRangeOfValue({
                  red: valueInRange(value, listLegend[2].red),
                  yellow: valueInRange(value, listLegend[2].yellow),
                  green: valueInRange(value, listLegend[2].green),
                }),
              }
            : null;
        },
      },
      {
        value: "4",
        getValue: (ouId) => {
          const value = dataNutri[ouId]?.find((rows) => {
            return rows[0] === "uEYUCTNMfdk";
          })?.[2];

          return value
            ? {
                de: "uEYUCTNMfdk",
                value: value,
                style: getStyleByRangeOfValue({
                  red: valueInRange(value, listLegend[3].red),
                  yellow: valueInRange(value, listLegend[3].yellow),
                  green: valueInRange(value, listLegend[3].green),
                }),
              }
            : null;
        },
      },
      {
        value: "5",
        getValue: (ouId) => {
          const value = dataNutri[ouId]?.find((rows) => {
            return rows[0] === "QOYBJGW55uA";
          })?.[2];

          return value
            ? {
                de: "QOYBJGW55uA",
                value: value,
                style: getStyleByRangeOfValue({
                  red: valueInRange(value, listLegend[4].red),
                  yellow: valueInRange(value, listLegend[4].yellow),
                  green: valueInRange(value, listLegend[4].green),
                }),
              }
            : null;
        },
      },
      {
        value: "6",
        getValue: (ouId) => {
          const value = dataNutri[ouId]?.find((rows) => {
            return rows[0] === "ivYamARkldu";
          })?.[2];

          return value
            ? {
                de: "ivYamARkldu",
                value: value,
                style: getStyleByRangeOfValue({
                  red: valueInRange(value, listLegend[5].red),
                  yellow: valueInRange(value, listLegend[5].yellow),
                  green: valueInRange(value, listLegend[5].green),
                }),
              }
            : null;
        },
      },
      {
        value: "7",
        getValue: (ouId) => {
          const value = dataNutri[ouId]?.find((rows) => {
            return rows[0] === "pS4nwJt7Qyv";
          })?.[2];

          return value
            ? {
                de: "pS4nwJt7Qyv",
                value: value,
                style: getStyleByRangeOfValue({
                  red: valueInRange(value, listLegend[6].red),
                  yellow: valueInRange(value, listLegend[6].yellow),
                  green: valueInRange(value, listLegend[6].green),
                }),
              }
            : null;
        },
      },
      {
        value: "8",
        getValue: (ouId) => {
          const value = dataNutri[ouId]?.find((rows) => {
            return rows[0] === "OWJYVDCxXm2";
          })?.[2];

          return value
            ? {
                de: "OWJYVDCxXm2",
                value: value,
                style: getStyleByRangeOfValue({
                  red: valueInRange(value, listLegend[7].red),
                  yellow: valueInRange(value, listLegend[7].yellow),
                  green: valueInRange(value, listLegend[7].green),
                }),
              }
            : null;
        },
      },
      {
        value: "9",
        getValue: (ouId) => {
          const value = dataNutri[ouId]?.find((rows) => {
            return rows[0] === "Exclusive breastfeeding";
          })?.[2];
          return value
            ? {
                de: "Exclusive breastfeeding",
                value: value,
                style: getStyleByRangeOfValue({
                  red: valueInRange(value, listLegend[8].red),
                  yellow: valueInRange(value, listLegend[8].yellow),
                  green: valueInRange(value, listLegend[8].green),
                }),
              }
            : null;
        },
      },
      {
        value: "10",
        getValue: (ouId) => {
          const value = dataNutri[ouId]?.find((rows) => {
            return rows[0] === "vitaminA";
          })?.[2];
          return value
            ? {
                de: "vitaminA",
                value: value,
                style: getStyleByRangeOfValue({
                  red: valueInRange(value, listLegend[9].red),
                  yellow: valueInRange(value, listLegend[9].yellow),
                  green: valueInRange(value, listLegend[9].green),
                }),
              }
            : null;
        },
      },
      {
        value: "11",
        getValue: (ouId) => {
          const value = dataNutri[ouId]?.find((rows) => {
            return rows[0] === "Deworming";
          })?.[2];
          return value
            ? {
                de: "Deworming",
                value: value,
                style: getStyleByRangeOfValue({
                  red: valueInRange(value, listLegend[10].red),
                  yellow: valueInRange(value, listLegend[10].yellow),
                  green: valueInRange(value, listLegend[10].green),
                }),
              }
            : null;
        },
      },
      {
        value: "12",
        getValue: (ouId) => {
          const value = dataNutri[ouId]?.find((rows) => {
            return rows[0] === "Children with SAM identified";
          })?.[2];
          return value
            ? {
                de: "Children with SAM identified",
                value: value,
                style: getStyleByRangeOfValue({
                  red: valueInRange(value, listLegend[11].red),
                  yellow: valueInRange(value, listLegend[11].yellow),
                  green: valueInRange(value, listLegend[11].green),
                }),
              }
            : null;
        },
      },
    ],
    [dataNutri, orgUnitGroups, orgUnits]
  );

  const listCapitalOrDistrict = useMemo(() => {
    return (
      dataNutri &&
      Object.keys(dataNutri)
        .map((ou) => orgUnits.find((item) => item.id === ou))
        .filter((ou) => ou.code)
        .sort(function (a, b) {
          if (a.code < b.code) {
            return -1;
          }
          if (a.code > b.code) {
            return 1;
          }
          return 0;
        })
    );
  }, [dataNutri]);

  useEffect(() => {
    (async () => {
      setLoading(true);

      // // Real data
      // const result = await pull("/api/getDashboard9WidgetKeyIndicators");
      // const nutriDataGroupByProvince = _.groupBy(result.data.rows, 1);

      // Mock data
      const metadatas = [
        "sfjQ42ahvLG",
        "tvtnT71idQ4",
        "vTHzAXlQiF6",
        "uEYUCTNMfdk",
        "QOYBJGW55uA",
        "ivYamARkldu",
        "pS4nwJt7Qyv",
        "OWJYVDCxXm2",
        "Exclusive breastfeeding",
        "vitaminA",
        "Deworming",
        "Children with SAM identified",
      ];

      const mockData = {
        W6sNfkJcXGC: [15, "0", 1, 70, 69, 17, 6, 71, 37, 62, 8, "NR"], // 01 Vientiane Capital
        YvLOmtTQD6b: [31, 10, 3, 48, 33, 20, 4, 88, 81, 54, 7, 0.8], // 02 Phongsali
        XKGgynPS1WZ: [59, 12, 3, 74, 60, 15, 7, 87, 96, 88, "0", 1.3], // 03 Louangnamtha
        rO2RVJWHpCe: [22, 10, 3, 61, 47, 29, 7, 93, 99, 35, 7, 1.3], // 04 Oudomxai
        FRmrFTE63D0: [96, 17, 9, 65, 62, 9, 6, 97, 96, 78, 19, 1.5], // 05 Bokeo
        MBZYTqkEgwf: [69, 15, 5, 53, 74, 14, 4, 96, 91, 99, 4, 1.1], // 06 Louangphabang
        hdeC7uX9Cko: [42, 12, 6, 66, 35, 14, 8, 98, 96, 57, 3, 0.9], // 07 Houaphan
        RdNV4tTRNEo: [36, 3, 1, 55, 89, 31, 6, 77, 73, 52, 12, 0.3], // 08 Xainyabouli
        VWGSudnonm5: [48, 25, 8, 53, 54, 9, 7, 89, 98, 49, 3, 1.5], // 09 Xiangkhouang
        quFXhkOJGB4: [27, 4, 2, 64, 83, 22, 4, 93, 99, 74, 7, 0.6], // 10 Vientiane
        vBWtCmNNnCG: [67, 11, 6, 54, 34, 14, 7, 90, 84, 86, 10, 0.6], // 11 Bolikhamxai
        c4HrGRJoarj: [70, 24, 9, 61, 83, 41, 10, 93, 95, 62, 8, 1.8], // 12 Khammouan
        TOgZ99Jv0bN: [67, 10, 6, 61, 21, 16, 10, 85, 81, 108, 1, 5.2], // 14 Salavan
        dOhqCNenSjS: [36, 25, 9, 47, 17, 24, 11, 98, 97, 94, 9, 3.2], // 15 Xekongxekong
        sv6c7CpPcrc: [46, 2, 1, 55, 20, 14, 5, 73, 86, 51, 4, 0.2], // 16 Champasak
        hRQsZhmvqgS: [48, 10, 4, 44, 44, 18, 11, 91, 93, 72, 1, 1.4], // 17 Attapu
        K27JzTKmBKh: [17, 4, 2, 55, 40, 23, 4, 97, 98, 46, 9, 0.9], // 18 Xaisomboun
        // pFCZqWnXtoU: [21, 4, 56, 63, 1, 7, 82, 42, 1, 12], // 13 Savannakhet
      };

      const mockmock = Object.keys(mockData).reduce((obj, ou) => {
        const data = metadatas.map((dx, idx) => {
          const curValueByMetadata = [dx, ou, mockData?.[ou]?.[idx] || ""];
          return curValueByMetadata;
        });

        return { ...obj, [ou]: data };
      }, {});

      console.log({ mockmock });

      setDataNutri(mockmock);

      setLoading(false);
    })();
  }, []);

  if (!dataNutri) return null;

  let stunnedDummy = [
    "13",
    "54",
    "69",
    "57",
    "19",
    "29",
    "29",
    "13",
    "20",
    "6",
    "20",
    "55",
    "35",
    "21",
    "25",
    "16",
  ];

  let wastedDummy = [
    "9",
    "23",
    "10",
    "22",
    "5",
    "8",
    "7",
    "5",
    "5",
    "3",
    "4",
    "13",
    "11",
    "11",
    "9",
    "6",
    "",
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        height: "100%",
      }}
    >
      <Table className="table-content">
        <TableHead
          sx={{
            "& th": {
              fontSize: "12px",
            },
          }}
        >
          <TableRow>
            <TableCell style={{ textAlign: "left" }}>
              {t("Key Indicators")}
            </TableCell>
            <TableCell colSpan={3}>{t("Growth Monitoring")}</TableCell>
            <TableCell colSpan={5}>
              {t("Maternal, Infant and Young Child Nutrition")}
            </TableCell>
            <TableCell></TableCell>
            <TableCell colSpan={2}>
              {t("Micronutrient supplementation")}
            </TableCell>
            <TableCell>IMAM</TableCell>
          </TableRow>
          <TableRow>
            <TableCell style={{ textAlign: "left" }}>{t("Province")}</TableCell>
            <TableCell>
              <div style={{ height: "100px" }}>
                {t("Children assessed for growth monitoring")}
              </div>
              <TooltipButton
                ranges={listLegend[0]}
                options={{ Infinity: 100 }}
              />
            </TableCell>
            <TableCell>
              <div style={{ height: "100px" }}>
                {t("Underweight among children under five years of age")}
              </div>
              <TooltipButton
                ranges={listLegend[1]}
                options={{ Infinity: 100 }}
              />
            </TableCell>
            <TableCell>
              <div style={{ height: "100px" }}>
                {t("Institutional Wasting")}
              </div>
              <TooltipButton
                ranges={listLegend[2]}
                options={{ Infinity: 100 }}
              />
            </TableCell>
            {/* <TableCell>
              <div style={{ height: "100px" }}>{t("Stunted")}</div>
              <TooltipButton
                ranges={listLegend[10]}
                options={{ Infinity: 100 }}
              />
            </TableCell>
            <TableCell>
              <div style={{ height: "100px" }}>{t("Wasted")}</div>
              <TooltipButton
                ranges={listLegend[11]}
                options={{ Infinity: 100 }}
              />
            </TableCell> */}
            <TableCell>
              <div style={{ height: "100px" }}>{t("ANC coverage")}</div>
              <TooltipButton
                ranges={listLegend[3]}
                options={{ Infinity: 100 }}
              />
            </TableCell>
            <TableCell>
              <div style={{ height: "100px" }}>
                {t("Pregnant women who received IFA tablets during ANC")}
              </div>
              <TooltipButton
                ranges={listLegend[4]}
                options={{ Infinity: 100 }}
              />
            </TableCell>
            <TableCell>
              <div style={{ height: "100px" }}>
                {t("Pregnant women identified as having anaemia")}
              </div>
              <TooltipButton
                ranges={listLegend[5]}
                options={{ Infinity: 100 }}
              />
            </TableCell>
            <TableCell>
              <div style={{ height: "100px" }}>{t("Low birth weight")}</div>
              <TooltipButton
                ranges={listLegend[6]}
                options={{ Infinity: 100 }}
              />
            </TableCell>
            <TableCell>
              <div style={{ height: "100px" }}>
                {t("Early initiation of breastfeeding")}
              </div>
              <TooltipButton
                ranges={listLegend[7]}
                options={{ Infinity: 100 }}
              />
            </TableCell>
            <TableCell>
              <div style={{ height: "100px" }}>
                {t("Exclusive breastfeeding until discharge")}
              </div>
              <TooltipButton
                ranges={listLegend[8]}
                options={{ Infinity: 100 }}
              />
            </TableCell>
            <TableCell>
              <div style={{ height: "100px" }}>
                {t("Vitamin-A supplementation for children aged 6-59 months")}
              </div>
              <TooltipButton
                ranges={listLegend[9]}
                options={{ Infinity: 100 }}
              />
            </TableCell>
            <TableCell>
              <div style={{ height: "100px" }}>
                {t("Deworming for children aged 12-59 months")}
              </div>
              <TooltipButton
                ranges={listLegend[10]}
                options={{ Infinity: 100 }}
              />
            </TableCell>
            <TableCell>
              <div style={{ height: "100px" }}>
                {t("# Children with SAM identified")}
              </div>
              <TooltipButton
                ranges={listLegend[11]}
                options={{ Infinity: 100 }}
              />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody
          sx={{
            "& td": {
              fontSize: "12px",
            },
          }}
        >
          {listCapitalOrDistrict.map((ou, index) => (
            <TableRow>
              <TableCell style={{ textAlign: "left", width: "9%" }}>
                {i18n.language === "lo"
                  ? ou.nameLo.slice(3)
                  : ou.nameEn.slice(3)}
              </TableCell>
              {listData.map((item) => {
                const { value, style, de } = item.getValue(ou.id) || {};
                let styleStunned;
                let styleWasted;
                if (de === "tvtnT71idQ4") {
                  if (wastedDummy[index] >= 0 && wastedDummy[index] <= 30.7) {
                    styleWasted = "#99E573";
                  }
                  if (
                    wastedDummy[index] >= 30.8 &&
                    wastedDummy[index] <= 36.4
                  ) {
                    styleWasted = "#FFDE59";
                  }
                  if (wastedDummy[index] >= 36.5) {
                    styleWasted = "#FF5757";
                  }
                }
                if (de === "tvtnT71idQ4") {
                  if (stunnedDummy[index] >= 0 && stunnedDummy[index] <= 30.7) {
                    styleStunned = "#99E573";
                  }
                  if (
                    stunnedDummy[index] >= 30.8 &&
                    stunnedDummy[index] <= 36.4
                  ) {
                    styleStunned = "#FFDE59";
                  }
                  if (stunnedDummy[index] >= 36.5) {
                    styleStunned = "#FF5757";
                  }
                }
                return (
                  <TableCell
                    style={{
                      background: style,
                      width: "7%",
                    }}
                  >
                    {item.getValue ? value : ""}
                    {value != "NR" && de !== "OWJYVDCxXm2" && value ? "%" : ""}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}

          <TableRow>
            <TableCell style={{ fontWeight: "bold", textAlign: "left" }}>
              {t("Total/ Lao PDR")}
            </TableCell>

            <TableCell style={{ fontWeight: "bold" }}> 41% </TableCell>
            <TableCell style={{ fontWeight: "bold" }}> 11% </TableCell>
            <TableCell style={{ fontWeight: "bold" }}> 5% </TableCell>
            <TableCell style={{ fontWeight: "bold" }}> 60% </TableCell>
            <TableCell style={{ fontWeight: "bold" }}> 59% </TableCell>
            <TableCell style={{ fontWeight: "bold" }}> 19% </TableCell>
            <TableCell style={{ fontWeight: "bold" }}> 7% </TableCell>
            <TableCell style={{ fontWeight: "bold" }}> 76% </TableCell>
            <TableCell style={{ fontWeight: "bold" }}> 74% </TableCell>
            <TableCell style={{ fontWeight: "bold" }}> 62% </TableCell>
            <TableCell style={{ fontWeight: "bold" }}> 6%</TableCell>
            <TableCell style={{ fontWeight: "bold" }}> 1.0%</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};

export default withWidgetChildrenLoader(Widget1);
